import React, { useState, useEffect, useCallback } from 'react';
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { API, clientsApi, packagesApi } from "@/api";
import Header from "@/components/Header";

const PackagesScreen = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customLessons, setCustomLessons] = useState('');
  const [customPricePerLesson, setCustomPricePerLesson] = useState('');

  const fetchClients = useCallback(async () => {
    const res = await clientsApi.getAll();
    setClients(res.data);
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const packages = [
    { type: 'trial', name: 'Lezione di Prova', lessons: 1, price: 0, icon: '🎁', color: 'emerald', desc: 'Prova gratuita per nuovi clienti' },
    { type: 'single', name: '1 Lezione', lessons: 1, price: 50, icon: '💪', color: 'blue', desc: 'Singola lezione personalizzata' },
    { type: 'three', name: '3 Lezioni', lessons: 3, price: 120, icon: '⭐', color: 'purple', desc: 'Risparmia €30 (€40/lezione)', savings: 30 },
    { type: 'five', name: '5 Lezioni', lessons: 5, price: 175, icon: '🏆', color: 'amber', desc: 'Risparmia €75 (€35/lezione)', savings: 75, popular: true },
    { type: 'ten', name: '10 Lezioni', lessons: 10, price: 300, icon: '🥇', color: 'red', desc: 'Risparmia €200 (€30/lezione)', savings: 200, best: true },
  ];

  const handlePurchaseClick = (pkgType) => {
    if (!selectedClient) {
      alert('Seleziona prima un cliente');
      return;
    }
    const pkg = packages.find(p => p.type === pkgType);
    const client = clients.find(c => c.id === selectedClient);
    setConfirmModal({ pkg, client });
  };

  const handleCustomPackageClick = () => {
    if (!selectedClient) {
      alert('Seleziona prima un cliente');
      return;
    }
    setCustomLessons('');
    setCustomPricePerLesson('');
    setShowCustomModal(true);
  };

  const confirmCustomPurchase = async () => {
    const lessonsCount = parseInt(customLessons);
    const pricePerLesson = parseFloat(customPricePerLesson);

    if (!lessonsCount || lessonsCount <= 0) {
      alert(t('common.fillAllFields'));
      return;
    }
    if (isNaN(pricePerLesson) || pricePerLesson < 0) {
      alert(t('common.fillAllFields'));
      return;
    }

    const client = clients.find(c => c.id === selectedClient);
    const totalPrice = lessonsCount * pricePerLesson;

    setConfirmModal({
      pkg: {
        type: 'custom',
        name: `${t('packages.customPackage')} (${lessonsCount} ${t('common.lessons')})`,
        lessons: lessonsCount,
        price: totalPrice,
        pricePerLesson: pricePerLesson,
        icon: '✨'
      },
      client,
      isCustom: true
    });
    setShowCustomModal(false);
  };

  const confirmPurchase = async () => {
    if (!confirmModal) return;
    setLoading(true);
    try {
      if (confirmModal.isCustom) {
        await axios.post(`${API}/packages/purchase`, {
          client_id: confirmModal.client.id,
          package_type: 'custom',
          custom_lessons: confirmModal.pkg.lessons,
          custom_price_per_lesson: confirmModal.pkg.pricePerLesson
        });
      } else {
        await packagesApi.purchase(confirmModal.client.id, confirmModal.pkg.type);
      }
      setSuccessMessage(t('packages.packagePurchased'));
      setConfirmModal(null);
      fetchClients();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || t('common.purchaseError'));
      clientsApi.getAll().then(res => setClients(res.data));
      setConfirmModal(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header title="Pacchetti" />
      
      {successMessage && (
        <div className="fixed top-20 left-4 right-4 bg-green-500 text-white p-4 rounded-xl shadow-lg z-50 text-center font-semibold">
          {successMessage}
        </div>
      )}

      {showCustomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Pacchetto Personalizzato</h3>
              <button 
                onClick={() => setShowCustomModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numero di Lezioni
                </label>
                <input
                  type="number"
                  min="1"
                  value={customLessons}
                  onChange={(e) => setCustomLessons(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Es: 7"
                  data-testid="custom-lessons-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzo per Lezione (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customPricePerLesson}
                  onChange={(e) => setCustomPricePerLesson(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Es: 35"
                  data-testid="custom-price-input"
                />
              </div>

              {customLessons && customPricePerLesson && (
                <div className="bg-indigo-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Totale pacchetto:</p>
                  <span className="text-2xl font-bold text-indigo-600">
                    €{(parseInt(customLessons) * parseFloat(customPricePerLesson)).toFixed(2)}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {customLessons} lezioni × €{parseFloat(customPricePerLesson).toFixed(2)}
                  </p>
                </div>
              )}

              <button
                onClick={confirmCustomPurchase}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
                data-testid="create-custom-package-btn"
              >
                Crea Pacchetto
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Conferma Acquisto</h3>
            <p className="text-gray-600 mb-2">
              Stai acquistando <strong>{confirmModal.pkg.name}</strong> per:
            </p>
            <p className="text-lg font-semibold text-gray-900 mb-4">
              {confirmModal.client.first_name} {confirmModal.client.last_name}
            </p>
            <div className="bg-indigo-50 rounded-xl p-4 mb-6 text-center">
              <span className="text-3xl font-bold text-indigo-600">
                {confirmModal.pkg.price === 0 ? 'Gratis' : `€${confirmModal.pkg.price.toFixed ? confirmModal.pkg.price.toFixed(2) : confirmModal.pkg.price}`}
              </span>
              {confirmModal.isCustom && (
                <p className="text-sm text-gray-500 mt-1">
                  {confirmModal.pkg.lessons} lezioni × €{confirmModal.pkg.pricePerLesson.toFixed(2)}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                disabled={loading}
              >
                Annulla
              </button>
              <button
                onClick={confirmPurchase}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-400"
                data-testid="confirm-purchase-btn"
              >
                {loading ? 'Acquisto...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('packages.client')}</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl"
            data-testid="select-client-package"
          >
            <option value="">{t('packages.selectClient')}</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.first_name} {client.last_name} ({client.remaining_lessons} {t('common.lessons')})
              </option>
            ))}
          </select>
        </div>

        <h2 className="text-xl font-bold text-gray-900 pt-2">{t('packages.availablePackages')}</h2>

        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-5 text-white">
          <div className="flex items-start gap-4">
            <div className="text-4xl">✨</div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">{t('packages.customPackage')}</h3>
              <p className="text-sm text-indigo-100">{t('packages.customPackageDesc')}</p>
            </div>
          </div>
          <button
            onClick={handleCustomPackageClick}
            className="w-full mt-4 bg-white text-indigo-600 px-4 py-3 rounded-lg font-semibold hover:bg-indigo-50 flex items-center justify-center gap-2"
            data-testid="buy-custom"
          >
            + {t('packages.createCustomPackage')}
          </button>
        </div>

        {packages.map(pkg => (
          <div
            key={pkg.type}
            className={`bg-white rounded-xl shadow-sm p-5 relative overflow-hidden ${
              pkg.popular ? 'ring-2 ring-amber-400' : pkg.best ? 'ring-2 ring-red-400' : ''
            }`}
          >
            {pkg.popular && (
              <div className="absolute top-0 right-0 bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                {t('packages.mostPopular')}
              </div>
            )}
            {pkg.best && (
              <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                {t('packages.bestValue')}
              </div>
            )}
            
            <div className="flex items-start gap-4">
              <div className="text-4xl">{pkg.icon}</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">{pkg.name}</h3>
                <p className="text-sm text-gray-500">{pkg.desc}</p>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div>
                <span className="text-2xl font-bold text-gray-900">
                  {pkg.price === 0 ? t('common.free') : `€${pkg.price}`}
                </span>
                {pkg.lessons > 1 && (
                  <span className="text-sm text-gray-500 ml-2">€{Math.round(pkg.price / pkg.lessons)}{t('packages.perLesson')}</span>
                )}
              </div>
              <button
                onClick={() => handlePurchaseClick(pkg.type)}
                className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg font-medium hover:bg-indigo-200 flex items-center gap-2"
                data-testid={`buy-${pkg.type}`}
              >
                {t('packages.buy')} &rarr;
              </button>
            </div>

            {pkg.savings && (
              <div className="mt-3 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2">
                {t('packages.youSave')} €{pkg.savings}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackagesScreen;
